import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError, AxiosResponse } from "axios";
import { FormConfiguration } from "../components/model/form-configuration";
import { url } from "../components/model/url-endpoint";
import { AutoCompleteOptions } from "../context/ApplicationContext";
import { logger } from "../utils/logger";

const initializeAutoCompleteOptions = (
  configuration: any
): AutoCompleteOptions => {
  const autoCompleteOptions: AutoCompleteOptions = {};

  // Iterate through all sections and fields in the configuration
  configuration.sections?.forEach((section: any) => {
    section.fields?.forEach((field: any) => {
      if (field.lookup_options && Array.isArray(field.lookup_options)) {
        // Initialize the field's autocomplete options
        autoCompleteOptions[field.id] = {
          originalOptions: field.lookup_options.map((option: any) => ({
            id: option.id || option.value,
            value: option.value || option.label || option.text,
          })),
          currentOptions: field.lookup_options.map((option: any) => ({
            id: option.id || option.value,
            value: option.value || option.label || option.text,
          })),
        };
      }
    });
  });

  return autoCompleteOptions;
};

const initializeFieldOptions = (configuration: any): { [fieldId: string]: any[] } => {
  const fieldOptions: { [fieldId: string]: any[] } = {};

  // Iterate through all sections and fields in the configuration
  configuration.sections?.forEach((section: any) => {
    section.fields?.forEach((field: any) => {
      if (field.lookup_options && Array.isArray(field.lookup_options)) {
        // Initialize the field's options for FIPS code filtering
        fieldOptions[field.id] = field.lookup_options.map((option: any) => ({
          id: option.id || option.value,
          value: option.value || option.label || option.text,
        }));
      }
    });
  });

  return fieldOptions;
};

const initialState: FormConfiguration & {
  autoCompleteOptions?: AutoCompleteOptions;
  fieldOptions?: { [fieldId: string]: any[] };
} = {
  form: {
    id: "",
    uuid: "",
    name: "",
    sections: [],
  },
  autoCompleteOptions: {},
  fieldOptions: {},
};

const formSlice = createSlice({
  name: "form",
  initialState,
  reducers: {
    setConfiguration: (state, action: PayloadAction<FormConfiguration>) => {
      const autoCompleteOptions = initializeAutoCompleteOptions(
        action.payload.form
      );
      const fieldOptions = initializeFieldOptions(action.payload.form);
      return {
        ...state,
        form: action.payload.form,
        autoCompleteOptions,
        fieldOptions,
      };
    },
  },
});

export const fetchFormConfiguration = (queryParams: URLSearchParams) => {
  return (dispatch: Dispatch<any>) => {
    axios
      .post(url + "get_configuration", {
        coi: queryParams.get("coi"),
        form: { uuid: queryParams.get("form") },
      })
      .then((response: AxiosResponse) => {
        dispatch(formActions.setConfiguration(response.data));
      })
      .catch((error: AxiosError) => {
        logger.error('Failed to fetch form configuration:', error);
      });
  };
};

export default formSlice;
export const formActions = formSlice.actions;
